import React, {useState, useEffect, useRef} from 'react';
import styles from './RangeSlider.module.scss';

interface RangeSliderProps {
    min: number;
    max: number;
    step: number;
    values: [number, number];
    onChange: (values: [number, number]) => void;
    disabled?: boolean;
    unit: string;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
    min, max, step, values, onChange, disabled = false, unit
}) => {
    const [minValue, setMinValue] = useState(values[0]);
    const [maxValue, setMaxValue] = useState(values[1]);
    const sliderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        onChange([minValue, maxValue]);
    }, [minValue, maxValue]);

    const getPercentage = (value: number) => ((value - min) / (max - min)) * 100;

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>, type: 'min' | 'max') => {
        event.preventDefault();

        const handleMouseMove = (e: MouseEvent) => {
            if (!sliderRef.current) {
                return;
            }
            const rect = sliderRef.current.getBoundingClientRect();
            const offsetX = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
            const percentage = offsetX / rect.width;
            const newValue = Math.round((percentage * (max - min) + min) / step) * step;

            if (type === 'min') {
                setMinValue(Math.max(min, Math.min(newValue, maxValue - step)));
            } else {
                setMaxValue(Math.min(max, Math.max(newValue, minValue + step)));
            }
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <div className={styles.sliderWrapper} ref={sliderRef}>
            <div className={styles.sliderTrack}>
                <div
                    className={styles.sliderRange}
                    style={{left: `${getPercentage(minValue)}%`, right: `${100 - getPercentage(maxValue)}%`}}
                />
                <div className={styles.sliderThumb}
                     style={{left: `calc(${getPercentage(minValue)}%)`}}
                     onMouseDown={(e) => handleMouseDown(e, 'min')}
                />
                <div className={styles.sliderThumb}
                     style={{left: `calc(${getPercentage(maxValue)}%)`}}
                     onMouseDown={(e) => handleMouseDown(e, 'max')}
                />
            </div>

            <div className={styles.sliderTooltip}
                 style={{left: `calc(${getPercentage(minValue)}%)`, top: '22px'}}
                 onMouseDown={(e) => handleMouseDown(e, 'min')}
            >
                <div className={styles.tooltipValue}>{minValue}</div>
                <div className={styles.tooltipArrow}></div>
            </div>

            <div className={styles.sliderTooltip}
                 style={{left: `calc(${getPercentage(maxValue)}%)`, top: '22px'}}
                 onMouseDown={(e) => handleMouseDown(e, 'max')}
            >
                <div className={styles.tooltipValue}>{maxValue}</div>
                <div className={styles.tooltipArrow}></div>
            </div>

            <div className={styles.sliderLabels}>
                <span>{min}{unit}</span>
                <span>{max}{unit}</span>
            </div>
        </div>
    );
};

export default RangeSlider;
